import { useMemo } from 'react';

import type { TicketWithComments } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';

import { TicketTypeOption } from '@features/tickets/components/ticketList/filter/ticketFilters.constants';

import { CloseAction, CommentAction } from '../ticketAction.constants';
import type { TicketActionOption } from '../ticketAction.utils';
import { useTicketActionAuthorization } from '../use-ticket-action-authorization/use-ticket-action-authorization';

interface UseTicketActionsHookArgs {
  ticket: TicketWithComments;
}

export function useTicketActions({ ticket }: UseTicketActionsHookArgs) {
  const { t } = useTranslation('ticket');

  const ticketType = ticket.type;
  const { canClose, canComment, canManageAlerts } =
    useTicketActionAuthorization(ticket);

  return useMemo<TicketActionOption[]>(() => {
    const possibleActionsInOrder = [
      CommentAction.Comment,
      CloseAction.ManageAlertConfiguration,
      CloseAction.Request,
      CloseAction.Close,
    ];

    return possibleActionsInOrder.reduce<TicketActionOption[]>(
      (options, actionType) => {
        const option: TicketActionOption = {
          text: t(`ticket:action.${actionType}`),
          value: actionType,
        };

        switch (actionType) {
          case CommentAction.Comment: {
            if (canComment) options.push(option);
            return options;
          }

          case CloseAction.ManageAlertConfiguration: {
            return canManageAlerts &&
              canClose &&
              canComment &&
              ticketType === TicketTypeOption.Incident
              ? [...options, option]
              : options;
          }

          case CloseAction.Request: {
            return canClose && canComment ? [...options, option] : options;
          }
          case CloseAction.Close: {
            return canClose ? [...options, option] : options;
          }

          default:
            return options;
        }
      },
      [],
    );
  }, [t, canComment, canClose, ticketType, canManageAlerts]);
}
